import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Clock, Loader2, XCircle } from "lucide-react";

import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { supportMailto, SUPPORT_EMAIL } from "@/lib/checkout";
import { Logo } from "@/components/logo";
import { Button } from "@/components/ui";

const REFRESH_KEYS = ["membership", "verification", "dashboard", "notifications", "unread", "connections"];

/** Paystack redirects here with ?reference= (and ?trxref=). The backend verifies with Paystack — never the browser. */
function useVerify(reference, enabled) {
  const qc = useQueryClient();
  const { refresh } = useAuth();
  const [state, setState] = useState({ phase: "checking" });
  useEffect(() => {
    if (!enabled || !reference) return undefined;
    let cancelled = false;
    let tries = 0;
    let timer;
    const check = async () => {
      try {
        const r = await api(`/payments/verify/${encodeURIComponent(reference)}`);
        if (cancelled) return;
        if (r.status === "initiated" || r.pending) {
          tries += 1;
          setState({ phase: tries < 6 ? "pending" : "pending_final" });
          if (tries < 6) timer = setTimeout(check, 3000);
          return;
        }
        setState({ phase: r.status === "success" ? "success" : "failed", status: r.status });
        REFRESH_KEYS.forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
        refresh();
      } catch (e) {
        if (!cancelled) setState({ phase: "error", message: e.message });
      }
    };
    check();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [reference, enabled, qc, refresh]);
  return state;
}

const COPY = {
  membership: { back: "/membership", backLabel: "View membership", success: "Your LOVE NET Premium is active. Enjoy sending requests and connecting on WhatsApp." },
  verification: { back: "/verification", backLabel: "Upload documents", success: "Payment confirmed. Now upload your ID and selfie so a reviewer can verify you." },
};

export default function PaymentCallback() {
  const [params] = useSearchParams();
  const { status } = useAuth();
  const navigate = useNavigate();
  const reference = params.get("reference") || params.get("trxref");
  const kind = reference?.startsWith("LNV-") ? "verification" : "membership";
  const c = COPY[kind];
  const state = useVerify(reference, status === "authed");

  let body;
  if (!reference) body = <Result testId="payment-callback-missing" Icon={XCircle} tone="error" title="No payment reference" text="We couldn't find a payment to confirm." />;
  else if (status === "guest") body = <Result testId="payment-callback-login" Icon={Clock} title="Log in to confirm your payment" text={`Reference ${reference}. Log in with the account you paid from.`} action={<Link to="/login" className="font-bold text-primary underline" data-testid="payment-callback-login-link">Log in</Link>} />;
  else if (state.phase === "checking" || state.phase === "pending" || status === "loading") body = <Result testId="payment-callback-checking" Icon={Loader2} spin title="Confirming your payment…" text={state.phase === "pending" ? "Paystack is still processing this payment. This can take a moment." : "Checking with Paystack. Please don't close this page."} />;
  else if (state.phase === "pending_final") body = <Result testId="payment-callback-pending" Icon={Clock} tone="gold" title="Payment still processing" text="We'll update your account automatically as soon as Paystack confirms it. You can check back on this page later." />;
  else if (state.phase === "success") body = <Result testId="payment-callback-success" Icon={CheckCircle2} tone="success" title="Payment successful" text={c.success} />;
  else if (state.phase === "failed") body = <Result testId="payment-callback-failed" Icon={XCircle} tone="error" title="Payment not completed" text={`Your payment ${state.status === "failed" ? "failed or was cancelled" : `is ${state.status}`}. If money left your account, contact support with the reference below.`} />;
  else body = <Result testId="payment-callback-error" Icon={XCircle} tone="error" title="We couldn't confirm this payment" text={state.message} />;

  const final = ["success", "failed", "error", "pending_final"].includes(state.phase);
  return (
    <div className="flex min-h-screen flex-col items-center bg-background px-6 py-10" data-testid="payment-callback-screen">
      <Logo />
      <div className="fade-up mt-10 w-full max-w-md rounded-[24px] border border-border bg-white p-8 text-center shadow-sm">
        {body}
        {reference ? <p className="mt-4 break-all font-mono text-xs text-muted-foreground" data-testid="payment-callback-reference">Ref {reference}</p> : null}
        {final && status === "authed" ? (
          <div className="mt-6 flex flex-col gap-2">
            <Button testId="payment-callback-continue" title={state.phase === "success" ? c.backLabel : "Back to checkout"} className="w-full" onClick={() => navigate(c.back, { replace: true })} />
            <a href={supportMailto(`Payment ${reference ?? ""}`)} data-testid="payment-callback-support" className="text-sm font-bold text-primary underline">Contact Support ({SUPPORT_EMAIL})</a>
          </div>
        ) : null}
      </div>
    </div>
  );
}

const TONES = { success: "text-[#3A6C54]", error: "text-[#9E3C3E]", gold: "text-[#C19B53]" };

function Result({ Icon, title, text, tone, spin, action, testId }) {
  return (
    <div data-testid={testId} role="status" aria-live="polite">
      <Icon className={`mx-auto h-12 w-12 ${TONES[tone] ?? "text-primary"} ${spin ? "animate-spin" : ""}`} />
      <h1 className="mt-4 text-[28px] leading-tight">{title}</h1>
      {text ? <p className="mt-2 text-sm text-muted-foreground">{text}</p> : null}
      {action ? <div className="mt-4">{action}</div> : null}
    </div>
  );
}
